import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminService } from '../services/adminService';

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={copy} className="ml-2 text-xs font-inter px-2 py-0.5 rounded"
      style={{ border: '1px solid #e5e7eb', color: copied ? '#16a34a' : '#979ba4', backgroundColor: '#fff' }}>
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function InfoRow({ label, value, copyable }) {
  if (!value) return null;
  return (
    <div className="flex items-start py-3" style={{ borderBottom: '1px solid #f3f4f6' }}>
      <span className="w-48 text-sm font-inter flex-shrink-0" style={{ color: '#979ba4' }}>{label}</span>
      <span className="text-sm font-inter font-medium flex-1" style={{ color: '#222324' }}>
        {value}
        {copyable && <CopyButton value={value} />}
      </span>
    </div>
  );
}

function StatusBadge({ label, color }) {
  const palettes = {
    green:  { bg: '#f0fdf4', text: '#16a34a', border: '#bbf7d0' },
    red:    { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' },
    yellow: { bg: '#fefce8', text: '#ca8a04', border: '#fde047' },
    gray:   { bg: '#f9fafb', text: '#6b7280', border: '#e5e7eb' },
  };
  const c = palettes[color] || palettes.gray;
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-inter font-medium"
      style={{ backgroundColor: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: '12px' }}>
      {label}
    </span>
  );
}

function statusColor(status) {
  if (!status) return 'gray';
  if (status === 'active') return 'green';
  if (status === 'inactive' || status === 'cancelled') return 'red';
  return 'yellow';
}

export default function BusinessLookup() {
  const [phone, setPhone] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const lookup = async () => {
    const q = phone.trim();
    if (!q) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const data = await adminService.lookupBusinessByPhone(q);
      setResult(data);
    } catch (e) {
      if (e.message && e.message.includes('404')) {
        setError(`No business found for ${q}`);
      } else {
        setError(e.message || 'Lookup failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') lookup(); };

  const user = result?.cognito_user;
  const voice = result?.voice;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f9f9fb' }}>
      {/* Nav */}
      <nav className="bg-white shadow-sm" style={{ borderBottom: '1px solid #e5e7eb' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16 space-x-4">
            <img src="/assets/logo---dark-with-color.png" alt="Remote Assistant" className="h-8 w-auto" />
            <Link to="/dashboard" className="text-sm font-inter" style={{ color: '#979ba4' }}>
              ← Dashboard
            </Link>
            <h1 className="text-xl font-montserrat font-semibold" style={{ color: '#222324' }}>
              Business Lookup
            </h1>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto py-6 px-4 sm:px-6 lg:px-8">

        {/* Search */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-6" style={{ border: '1px solid #e5e7eb' }}>
          <h2 className="text-lg font-montserrat font-semibold mb-4" style={{ color: '#222324' }}>
            Lookup by assistant phone number
          </h2>
          <div className="flex space-x-3">
            <input
              type="text"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="+12505551234"
              className="flex-1 px-3 py-2 text-sm font-inter rounded-lg"
              style={{ border: '1px solid #e5e7eb', outline: 'none' }}
            />
            <button
              onClick={lookup}
              disabled={loading || !phone.trim()}
              className="px-4 py-2 text-sm font-inter font-medium rounded-lg text-white"
              style={{ backgroundColor: loading ? '#6b9cfd' : '#055efd' }}>
              {loading ? 'Searching…' : 'Lookup'}
            </button>
          </div>
          {error && (
            <p className="mt-3 text-sm font-inter" style={{ color: '#dc2626' }}>{error}</p>
          )}
        </div>

        {/* Result */}
        {result && (
          <div className="bg-white rounded-2xl shadow-sm p-6" style={{ border: '1px solid #e5e7eb' }}>
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-xl font-montserrat font-semibold" style={{ color: '#222324' }}>
                  {result.business_name || '—'}
                </h2>
                <p className="text-sm font-inter mt-1" style={{ color: '#979ba4' }}>
                  {result.business_id}
                </p>
              </div>
              {user && (
                <StatusBadge
                  label={user.subscription_status || 'unknown'}
                  color={statusColor(user.subscription_status)}
                />
              )}
            </div>

            {/* Business details */}
            <p className="text-xs font-inter font-semibold uppercase tracking-wide mb-2"
              style={{ color: '#979ba4' }}>Business</p>
            <div className="mb-6">
              <InfoRow label="Business ID"    value={result.business_id}    copyable />
              <InfoRow label="Assistant phone" value={result.assistant_phone} copyable />
              <InfoRow label="Customer phone"  value={result.phone_number} />
              {voice && (
                <InfoRow label="Voice"
                  value={`${voice.provider} / ${voice.voice_id}${voice.sample_rate ? ` (${voice.sample_rate} Hz)` : ''}`} />
              )}
            </div>

            {/* Cognito user */}
            {user ? (
              <>
                <p className="text-xs font-inter font-semibold uppercase tracking-wide mb-2"
                  style={{ color: '#979ba4' }}>Cognito account</p>
                <div className="mb-6">
                  <InfoRow label="Login email"   value={user.email}    copyable />
                  <InfoRow label="Name"           value={user.name} />
                  <InfoRow label="Status"         value={user.status} />
                  <InfoRow label="Enabled"        value={user.enabled ? 'Yes' : 'No'} />
                  <InfoRow label="Subscription"   value={user.subscription_status} />
                  <InfoRow label="Prepaid minutes" value={user.prepaid_minutes != null ? String(user.prepaid_minutes) : null} />
                  {user.prepaid_expiry && <InfoRow label="Prepaid expiry" value={user.prepaid_expiry} />}
                  <InfoRow label="Admin"          value={user.admin === 'true' ? 'Yes' : 'No'} />
                  <InfoRow label="Created"        value={user.created ? new Date(user.created).toLocaleString() : null} />
                </div>
              </>
            ) : (
              <p className="text-sm font-inter mb-6" style={{ color: '#979ba4' }}>
                Cognito user details unavailable
              </p>
            )}

            {/* Quick actions */}
            <div className="flex flex-wrap gap-3 pt-4" style={{ borderTop: '1px solid #f3f4f6' }}>
              {user && (
                <button
                  onClick={() => navigate('/users', { state: { searchEmail: user.email } })}
                  className="px-4 py-2 text-sm font-inter font-medium rounded-lg"
                  style={{ border: '1px solid #055efd', color: '#055efd', backgroundColor: '#fff' }}>
                  Open in User Manager
                </button>
              )}
              <button
                onClick={() => navigate('/ledger', { state: { businessId: result.business_id } })}
                className="px-4 py-2 text-sm font-inter font-medium rounded-lg"
                style={{ border: '1px solid #055efd', color: '#055efd', backgroundColor: '#fff' }}>
                Open in Ledger Manager
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
